import { HEN_BREEDS } from '../data/breeds';
import type { BreedId, HenSpecialId, RoosterPassiveId, Stat } from '../data/types';
import type { Flock, GameState, Hen } from './types';

/**
 * Breed powers and rooster passives: implementation and player-facing text
 * live side by side so they can't drift apart. Every HenSpecialId and
 * RoosterPassiveId must have an entry (enforced by the Record types) and a
 * behavior test (tests/unit/specials.test.ts).
 *
 * Stat effects go through `ModSink`; effects that aren't stats (per-egg
 * rolls, event spawning, combo ramp, wrangler thresholds) are queried by the
 * owning system via the helpers at the bottom of this file.
 */

export interface ModSink {
  /** This hen only. */
  hen(stat: Stat, op: 'mul' | 'add', value: number): void;
  /** Every hen in this hen's flock. */
  flock(stat: Stat, op: 'mul' | 'add', value: number): void;
  /** The whole farm. */
  global(stat: Stat, op: 'mul' | 'add', value: number): void;
}

export interface SpecialCtx {
  state: GameState;
  flock: Flock;
  hen: Hen;
  wall: number;
  localHour: number;
  /** Hens in this flock with the same special. */
  countInFlock: number;
  /** Hens on the farm with the same special. */
  countInFarm: number;
  /** True for the first hen (in scan order) with this special in her flock / on the farm. */
  firstInFlock: boolean;
  firstInFarm: boolean;
}

export interface HenSpecialDef {
  name: string;
  description: string;
  apply?: (ctx: SpecialCtx, sink: ModSink) => void;
}

const lifetimeTens = (s: GameState) => Math.max(0, Math.log10(Math.max(1, s.lifetimeEarned)) - 2);
const flockHasBreed = (f: Flock, breed: BreedId) => f.boxes.some((b) => b.hen?.breed === breed);

export const HEN_SPECIALS: Record<HenSpecialId, HenSpecialDef> = {
  record_keeper: {
    name: 'Record Keeper',
    description: 'Her eggs are worth 2% more for every ×10 of lifetime earnings past $100 (max +20%).',
    apply: (c, s) => s.hen('value', 'mul', 1 + Math.min(0.2, 0.02 * lifetimeTens(c.state))),
  },
  utility_broody: {
    name: 'Utility Hen',
    description: 'Broody hens in her flock succeed 10 percentage points more often.',
    apply: (c, s) => { if (c.firstInFlock) s.flock('broodSuccess', 'add', 0.1); },
  },
  silver_egg_chance: {
    name: 'Silver Streak',
    description: 'Silver eggs spawn 10% more often per Wyandotte hen (max +50%).',
    apply: (c, s) => { if (c.firstInFarm) s.global('silverSpawn', 'mul', 1 + Math.min(0.5, 0.1 * c.countInFarm)); },
  },
  broody_hatcher: {
    name: 'Broody Hatcher',
    description: 'Her brood eggs are worth double.',
    apply: (_c, s) => s.hen('hatchReward', 'mul', 2),
  },
  golden_egg_affinity: {
    name: 'Golden Affinity',
    description: 'Golden eggs spawn 50% more often while you own a Welsummer.',
    apply: (c, s) => { if (c.firstInFarm) s.global('goldenSpawn', 'mul', 1.5); },
  },
  accelerant: {
    name: 'Accelerant',
    description: 'Every hen in her flock lays 5% faster per New Hampshire Red (max +25%).',
    apply: (c, s) => { if (c.firstInFlock) s.flock('lay', 'mul', 1 + Math.min(0.25, 0.05 * c.countInFlock)); },
  },
  blue_eggs_silver: {
    name: 'Blue Ribbon',
    description: 'Silver eggs pay 25% more per Ameraucana (max +100%).',
    apply: (c, s) => { if (c.firstInFarm) s.global('silverPayout', 'mul', 1 + Math.min(1, 0.25 * c.countInFarm)); },
  },
  chocolate_eggs: {
    name: 'Chocolate Eggs',
    description: 'Each egg has a 10% chance to be a deep chocolate egg worth 3×.',
  },
  rainbow_egg_spawner: {
    name: 'Rainbow Layer',
    description: 'Rainbow eggs can appear while you own an Easter Egger. Each extra Easter Egger makes them 25% more common (max 3×).',
  },
  paired_bonus: {
    name: 'Paired Layer',
    description: 'Her eggs are worth 50% more when a White Leghorn shares her flock.',
    apply: (c, s) => { if (flockHasBreed(c.flock, 'LEGHORN')) s.hen('value', 'mul', 1.5); },
  },
  achievement_affinity: {
    name: 'Achiever',
    description: 'Hen Wrangler upgrades need 20% fewer achievements while you own a Barnevelder.',
  },
  master_broody: {
    name: 'Master Brooder',
    description: 'Her broods always succeed and her brood eggs are worth double.',
    apply: (_c, s) => { s.hen('broodSuccess', 'add', 1); s.hen('hatchReward', 'mul', 2); },
  },
  gentle_giant: {
    name: 'Gentle Giant',
    description: 'Every hen in her flock earns 3% more per Bielefelder (max +30%).',
    apply: (c, s) => { if (c.firstInFlock) s.flock('value', 'mul', 1 + Math.min(0.3, 0.03 * c.countInFlock)); },
  },
  midnight_layer: {
    name: 'Midnight Layer',
    description: 'Her eggs are worth 50% more between 8 pm and 6 am (your local time).',
    apply: (c, s) => { if (c.localHour >= 20 || c.localHour < 6) s.hen('value', 'mul', 1.5); },
  },
  olive_eggs: {
    name: 'Olive Eggs',
    description: 'Each egg has a 15% chance to be a prized olive egg worth 2×.',
  },
  loyalty_bonus: {
    name: 'Loyal',
    description: "Her eggs are worth 1% more for each day she's lived on your farm (max +30%).",
    apply: (c, s) => {
      const days = Math.max(0, (c.wall - c.hen.placedWall) / 86_400_000);
      s.hen('value', 'mul', 1 + Math.min(0.3, 0.01 * days));
    },
  },
  dark_aura: {
    name: 'Dark Aura',
    description: 'After 30 minutes of continuous play, every hen in her flock earns 20% more.',
    apply: (c, s) => {
      if (c.firstInFlock && c.wall - c.state.sessionStartWall >= 30 * 60_000) s.flock('value', 'mul', 1.2);
    },
  },
  blind_spot: {
    name: 'Showstopper',
    description: 'She lays 20% slower, but your click combos build 50% faster while you own a Polish.',
    apply: (_c, s) => s.hen('lay', 'mul', 0.8),
  },
  frizzled_luck: {
    name: 'Frizzled Luck',
    description: 'Critters show up 20% more often per Frizzle (max +60%).',
    apply: (c, s) => { if (c.firstInFarm) s.global('critterSpawn', 'mul', 1 + Math.min(0.6, 0.2 * c.countInFarm)); },
  },
  royal_crest: {
    name: 'Royal Crest',
    description: 'With at least two RARE hens in her flock, every hen there earns 15% more.',
    apply: (c, s) => {
      if (!c.firstInFlock) return;
      const rare = c.flock.boxes.filter((b) => b.hen && HEN_BREEDS[b.hen.breed].tier === 'RARE').length;
      if (rare >= 2) s.flock('value', 'mul', 1.15);
    },
  },
  flock_synergy: {
    name: 'Steady Presence',
    description: 'Every hen in her flock lays 3% faster per Brahma (max +30%).',
    apply: (c, s) => { if (c.firstInFlock) s.flock('lay', 'mul', 1 + Math.min(0.3, 0.03 * c.countInFlock)); },
  },
  warmth_aura_broody: {
    name: 'Warm Feathers',
    description: 'Broods in her flock finish 30% faster.',
    apply: (c, s) => { if (c.firstInFlock) s.flock('broodDuration', 'mul', 0.7); },
  },
  adjacency_serama: {
    name: 'Bantam Buddies',
    description: 'Her eggs are worth 25% more when a Serama shares her flock.',
    apply: (c, s) => { if (flockHasBreed(c.flock, 'SERAMA')) s.hen('value', 'mul', 1.25); },
  },
  rare_collector: {
    name: 'Collector',
    description: 'Every hen in her flock earns 5% more per other RARE breed in the flock (max +50%).',
    apply: (c, s) => {
      if (!c.firstInFlock) return;
      const others = new Set<BreedId>();
      for (const b of c.flock.boxes) {
        if (b.hen && b.hen.breed !== c.hen.breed && HEN_BREEDS[b.hen.breed].tier === 'RARE') others.add(b.hen.breed);
      }
      s.flock('value', 'mul', 1 + Math.min(0.5, 0.05 * others.size));
    },
  },
  bloom_event: {
    name: 'Thousand Flowers',
    description: 'Her flock sprouts a bloom egg every 2 minutes (every minute with a Mille Fleur rooster).',
  },
};

export interface RoosterPassiveDef {
  description: (aura: number) => string;
  /** Effects on the rooster's own flock (flock scope) and/or the farm (global). */
  apply?: (ctx: { state: GameState; flock: Flock; first: boolean }, sink: Pick<ModSink, 'flock' | 'global'>) => void;
}

const auraText = (aura: number) => `Every hen in his flock earns ×${aura.toFixed(2)}.`;

export const ROOSTER_PASSIVES: Record<RoosterPassiveId, RoosterPassiveDef> = {
  steady_provider: {
    description: (a) => `${auraText(a)} Auto-gathered eggs from his flock are worth 30% more.`,
    apply: (_c, s) => s.flock('autoValue', 'mul', 1.3),
  },
  nervous_energy: {
    description: (a) => `${auraText(a)} Eggs you click in his flock are worth 50% more.`,
    apply: (_c, s) => s.flock('clickValue', 'mul', 1.5),
  },
  nest_warmth: { description: (a) => `${auraText(a)} Broody-prone hens in his flock go broody sooner (laying phase ×0.7).` },
  silver_eye: {
    description: (a) => `${auraText(a)} Silver eggs spawn twice as often, farm-wide.`,
    apply: (c, s) => { if (c.first) s.global('silverSpawn', 'mul', 2); },
  },
  record_crow: { description: (a) => `${auraText(a)} His aura grows 2% for every ×10 of lifetime earnings past $100 (max +20%).` },
  disciplined_flock: {
    description: (a) => `${auraText(a)} Clickable events last 25% longer, farm-wide. Hens in his flock go broody half as often.`,
    apply: (c, s) => { if (c.first) s.global('eventDuration', 'mul', 1.25); },
  },
  dawn_call: {
    description: (a) => `${auraText(a)} Golden eggs spawn 25% more often, farm-wide.`,
    apply: (c, s) => { if (c.first) s.global('goldenSpawn', 'mul', 1.25); },
  },
  silk_brooder: { description: (a) => `${auraText(a)} Broody-prone hens in his flock go broody much sooner (laying phase ×0.6).` },
  warmth_call: { description: (a) => `${auraText(a)} Broody-prone hens in his flock go broody the fastest (laying phase ×0.5).` },
  bloom_call: { description: (a) => `${auraText(a)} Mille Fleur hens in his flock bloom twice as often.` },
  aura_only: { description: (a) => auraText(a) },
};

/** Rooster aura, including Australorp's lifetime scaling. */
export function roosterAura(state: GameState, baseAura: number, passive: RoosterPassiveId): number {
  return passive === 'record_crow' ? baseAura + Math.min(0.2, 0.02 * Math.floor(lifetimeTens(state))) : baseAura;
}

// ── Non-stat special queries (used by events, collection, combo, requirements) ──

export function countSpecial(state: GameState, id: HenSpecialId): number {
  let n = 0;
  for (const f of state.flocks) for (const b of f.boxes) if (b.hen && HEN_BREEDS[b.hen.breed].special === id) n++;
  return n;
}

export function flockSpecialCount(flock: Flock, id: HenSpecialId): number {
  return flock.boxes.reduce((n, b) => n + (b.hen && HEN_BREEDS[b.hen.breed].special === id ? 1 : 0), 0);
}

/** Per-egg value roll for chocolate / olive eggs. Returns the multiplier and a label if it procs. */
export function eggRoll(hen: Hen, roll: number): { mult: number; label: string | null } {
  const special = HEN_BREEDS[hen.breed].special;
  if (special === 'chocolate_eggs' && roll < 0.1) return { mult: 3, label: 'Chocolate egg!' };
  if (special === 'olive_eggs' && roll < 0.15) return { mult: 2, label: 'Olive egg!' };
  return { mult: 1, label: null };
}

/** Combo ramp speed (Polish). */
export function comboRampMult(state: GameState): number {
  return countSpecial(state, 'blind_spot') > 0 ? 1.5 : 1;
}

/** Rainbow egg spawn multiplier (0 = none possible). */
export function rainbowSpawnMult(state: GameState): number {
  const n = countSpecial(state, 'rainbow_egg_spawner');
  return n === 0 ? 0 : Math.min(3, 1 + 0.25 * (n - 1));
}

/** Bloom interval for a flock in seconds, or null if the flock has no Mille Fleur hen. */
export function bloomIntervalSec(flock: Flock, base: number, paired: number): number | null {
  if (flockSpecialCount(flock, 'bloom_event') === 0) return null;
  return flock.rooster?.breed === 'MILLE_FLEUR_DUCCLE' ? paired : base;
}

/** Barnevelder lowers Hen Wrangler achievement thresholds. */
export function wranglerThresholdMult(state: GameState, cut: number): number {
  return countSpecial(state, 'achievement_affinity') > 0 ? 1 - cut : 1;
}
